import { useCallback, useState } from 'react';
import OpenAI from 'openai';

const useAIHelpers = ({
  code,
  setCode,
  selectedShape,
  editorRef,
  monacoRef,
  shapeCodeCacheRef,
  isUpdatingEditorRef,
}) => {
  const [aiStatus, setAiStatus] = useState({ enabled: false, loading: false, error: null });
  const [showAIPrompt, setShowAIPrompt] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [isGeneratingCode, setIsGeneratingCode] = useState(false);
  const [aiMode, setAiMode] = useState('generate');
  const [selectedCode, setSelectedCode] = useState(null);

  const getOpenAIKey = useCallback(() => {
    const isProd = process.env.NODE_ENV === 'production';
    const allowBrowserKey = process.env.REACT_APP_ALLOW_OPENAI_IN_BROWSER === 'true';
    if (process.env.REACT_APP_OPENAI_API_KEY) {
      if (isProd && !allowBrowserKey) {
        return null;
      }
      return process.env.REACT_APP_OPENAI_API_KEY;
    }
    if (isProd) {
      return null;
    }
    try {
      const storedKey = localStorage.getItem('openai_api_key');
      if (storedKey) {
        return storedKey;
      }
    } catch (error) {
    }
    return null;
  }, []);

  const getAICodeSuggestions = useCallback(
    async (context, position) => {
      const apiKey = getOpenAIKey();
      if (!apiKey) {
        return [];
      }

      try {
        setAiStatus((prev) => ({ ...prev, loading: true }));

        const openai = new OpenAI({
          apiKey: apiKey,
          dangerouslyAllowBrowser: true,
        });

        const linesBefore = context.split('\n').slice(Math.max(0, position.lineNumber - 10), position.lineNumber);
        const currentLine = context.split('\n')[position.lineNumber - 1] || '';
        const prefix = currentLine.substring(0, position.column - 1);

        const prompt = `Sen bir PHP kod tamamlama asistanısın. Kullanıcı şu anda şu kod üzerinde çalışıyor:

\`\`\`php
${linesBefore.join('\n')}
${prefix}█
\`\`\`

Kullanıcının yazmaya çalıştığı kodu tamamla. Sadece kod önerisi ver, açıklama yapma. Maksimum 3 öneri ver. Her öneri kısa ve öz olsun (maksimum 50 karakter).`;

        const completion = await openai.chat.completions.create({
          model: process.env.REACT_APP_OPENAI_MODEL || 'gpt-4o',
          messages: [
            { role: 'system', content: 'Sen bir PHP kod tamamlama asistanısın. Kısa ve öz kod önerileri ver.' },
            { role: 'user', content: prompt },
          ],
          max_tokens: 100,
          temperature: 0.3,
        });

        const suggestions =
          completion.choices[0]?.message?.content?.trim().split('\n').filter((s) => s.trim()) || [];

        setAiStatus((prev) => ({ ...prev, loading: false }));

        return suggestions.slice(0, 3).map((suggestion, index) => ({
          label: suggestion.replace(/^[-*]\s*/, '').trim(),
          kind: monacoRef.current?.languages?.CompletionItemKind?.Function || 1,
          insertText: suggestion.replace(/^[-*]\s*/, '').trim(),
          detail: 'AI Önerisi',
          range: {
            startLineNumber: position.lineNumber,
            startColumn: position.column,
            endLineNumber: position.lineNumber,
            endColumn: position.column,
          },
          sortText: `ai_${index}`,
        }));
      } catch (error) {
        setAiStatus((prev) => ({ ...prev, loading: false, error: error.message }));
        return [];
      }
    },
    [getOpenAIKey, monacoRef],
  );

  const generateCodeFromPrompt = useCallback(
    async (prompt, currentCode = '', mode = 'generate', codeToModify = null) => {
      const apiKey = getOpenAIKey();
      if (!apiKey) {
        setAiStatus({
          enabled: false,
          loading: false,
          error: "OpenAI API key bulunamadı. Lütfen API key'inizi ayarlayın.",
        });
        return null;
      }

      try {
        setIsGeneratingCode(true);
        setAiStatus((prev) => ({ ...prev, loading: true, error: null }));

        const openai = new OpenAI({
          apiKey: apiKey,
          dangerouslyAllowBrowser: true,
        });

        let userPrompt;

        if (mode === 'modify' && codeToModify) {
          userPrompt = `${prompt}

Mevcut Kod:
\`\`\`php
${codeToModify}
\`\`\``;
        } else {
          userPrompt = `${prompt}

Mevcut Kod:
\`\`\`php
${currentCode}
\`\`\``;
        }

        const model = process.env.REACT_APP_OPENAI_MODEL || 'gpt-4o';
        
        const completion = await openai.chat.completions.create({
          model,
          messages: [
            { role: 'user', content: userPrompt },
          ],
          max_tokens: 1500,
          temperature: 0.9,
        });

        const generatedCode = completion.choices[0]?.message?.content?.trim() || '';
        const cleanedCode = generatedCode.replace(/^```php\s*/i, '').replace(/```\s*$/, '').trim();

        setIsGeneratingCode(false);
        setAiStatus((prev) => ({ ...prev, loading: false }));

        return cleanedCode;
      } catch (error) {
        setIsGeneratingCode(false);
        setAiStatus((prev) => ({ ...prev, loading: false, error: error.message }));
        return null;
      }
    },
    [getOpenAIKey],
  );

  const handleGenerateCode = useCallback(
    async () => {
      if (!aiPrompt.trim()) return;

      const currentCode = code || '';
      let newCode = '';

      if (aiMode === 'modify' && selectedCode && selectedCode.text) {
        const modifiedCode = await generateCodeFromPrompt(aiPrompt, currentCode, 'modify', selectedCode.text);

        if (modifiedCode) {
          if (editorRef.current && selectedCode.range) {
            try {
              const monaco = monacoRef.current;
              const range = new monaco.Range(
                selectedCode.range.startLineNumber,
                selectedCode.range.startColumn,
                selectedCode.range.endLineNumber,
                selectedCode.range.endColumn,
              );

              const edit = {
                range: range,
                text: modifiedCode,
              };

              editorRef.current.executeEdits('ai-modify', [edit]);

              const lastLine = modifiedCode.split('\n').length;
              const lastLineLength = modifiedCode.split('\n')[lastLine - 1]?.length || 0;
              const newPosition = {
                lineNumber: selectedCode.range.startLineNumber + lastLine - 1,
                column: lastLine === 1 ? selectedCode.range.startColumn + lastLineLength : lastLineLength + 1,
              };
              editorRef.current.setPosition(newPosition);

              const updatedCode = editorRef.current.getValue();
              isUpdatingEditorRef.current = true;
              setCode(updatedCode);

              if (selectedShape) {
                shapeCodeCacheRef.current[selectedShape] = updatedCode;
              }

              setTimeout(() => {
                isUpdatingEditorRef.current = false;
              }, 100);

              setAiPrompt('');
              setSelectedCode(null);
              setAiMode('generate');
              setShowAIPrompt(false);
              return;
            } catch (error) {
            }
          }

          if (selectedCode.startOffset !== undefined && selectedCode.endOffset !== undefined) {
            const beforeSelection = currentCode.substring(0, selectedCode.startOffset);
            const afterSelection = currentCode.substring(selectedCode.endOffset);
            newCode = beforeSelection + modifiedCode + afterSelection;

            if (selectedShape) {
              shapeCodeCacheRef.current[selectedShape] = newCode;
            }

            if (editorRef.current) {
              isUpdatingEditorRef.current = true;
              editorRef.current.setValue(newCode);
              setCode(newCode);
              setTimeout(() => {
                isUpdatingEditorRef.current = false;
              }, 100);
            }
          } else {
            return;
          }
        }
      } else {
        const generatedCode = await generateCodeFromPrompt(aiPrompt, currentCode, 'generate');
        if (generatedCode) {
          newCode = currentCode ? `${currentCode}\n\n${generatedCode}` : generatedCode;
        }
      }

      if (newCode) {
        isUpdatingEditorRef.current = true;
        setCode(newCode);
        if (editorRef.current) {
          editorRef.current.setValue(newCode);
        }

        if (selectedShape) {
          shapeCodeCacheRef.current[selectedShape] = newCode;
        }

        setTimeout(() => {
          isUpdatingEditorRef.current = false;
        }, 100);

        setAiPrompt('');
        setSelectedCode(null);
        setAiMode('generate');
        setShowAIPrompt(false);
      }
    },
    [
      aiPrompt,
      code,
      aiMode,
      selectedCode,
      selectedShape,
      generateCodeFromPrompt,
      editorRef,
      monacoRef,
      setCode,
      shapeCodeCacheRef,
      isUpdatingEditorRef,
    ],
  );

  const handleOpenAIPrompt = useCallback(() => {
    if (editorRef.current) {
      const selection = editorRef.current.getSelection();
      const model = editorRef.current.getModel();

      if (selection && !selection.isEmpty()) {
        const selectedText = model.getValueInRange(selection);
        const startOffset = model.getOffsetAt(selection.getStartPosition());
        const endOffset = model.getOffsetAt(selection.getEndPosition());

        setSelectedCode({
          text: selectedText,
          startOffset: startOffset,
          endOffset: endOffset,
          range: {
            startLineNumber: selection.startLineNumber,
            startColumn: selection.startColumn,
            endLineNumber: selection.endLineNumber,
            endColumn: selection.endColumn,
          },
        });
        setAiMode('modify');
      } else {
        setSelectedCode(null);
        setAiMode('generate');
      }
    } else {
      setSelectedCode(null);
      setAiMode('generate');
    }

    setShowAIPrompt(true);
  }, [editorRef]);

  return {
    aiStatus,
    setAiStatus,
    showAIPrompt,
    setShowAIPrompt,
    aiPrompt,
    setAiPrompt,
    isGeneratingCode,
    aiMode,
    setAiMode,
    selectedCode,
    setSelectedCode,
    getOpenAIKey,
    getAICodeSuggestions,
    generateCodeFromPrompt,
    handleGenerateCode,
    handleOpenAIPrompt,
  };
};

export default useAIHelpers;
